<script setup lang="ts">
import type { IPricingCardData } from '@/models'
import { config } from '../../../config'
const { getLocalizedPathPrefix } = useLocalization()

export interface TPricingCardProps {
  lcid: string
  model: IPricingCardData
}

const props = withDefaults(defineProps<TPricingCardProps>(), {})

const { defaultCheckoutConfig, otherCheckoutConfig } = useCountryInfo()

const borderCssClass = () => {
  if (props.model.highlight) {
    return 'border-2 border-primary'
  }
  return 'border border-transparent'
}

const parseItem = (item: string): { text: string; noteNumber: number } => {
  if (item.indexOf('^') === -1) {
    return {
      text: item,
      noteNumber: -1,
    }
  }
  const arr = item.split('^')
  return {
    text: arr[0] || '',
    noteNumber: Number(arr[1]),
  }
}
</script>
<template>
  <div class="rounded-3xl p-8 xl:p-10" :class="borderCssClass()">
    <div class="flex items-center justify-between gap-x-4">
      <h3 class="text-lg/8 font-semibold text-primary">
        {{ model.title }}
      </h3>
      <!-- <p class="rounded-full bg-primary bg-opacity-50 px-2.5 py-1 text-xs/5 font-semibold text-primary">
        Most popular
      </p> -->
    </div>
    <p class="text-sm/6">
      {{ model.description }}
    </p>

    <p class="mt-4 mb-8 flex items-baseline gap-x-1">
      <span v-if="model.startingAt" class="text-sm/6 font-semibold">
        {{ model.startingAt }}
      </span>
      <span class="text-4xl font-semibold tracking-tight">
        {{ model.price }}
      </span>
      <span v-if="model.pricePer" class="text-sm/6 font-semibold">
        {{ `/${model.pricePer}` }}
      </span>
    </p>

    <p v-if="model.contains" class="my-8 text-sm/6 font-semibold">
      {{ model.contains }}
    </p>

    <ul role="list" class="space-y-3 text-sm/6">
      <li v-for="(item, index) in model.featuresIncluded" :key="`includes-${index}`" class="flex gap-x-3">
        <svg
          class="h-6 w-5 flex-none text-primary"
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden="true"
          data-slot="icon"
        >
          <path
            fill-rule="evenodd"
            d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z"
            clip-rule="evenodd"
          />
        </svg>
        <span
          >{{ parseItem(item).text
          }}<sup class="sups" v-if="parseItem(item).noteNumber > -1"
            ><a :href="`#fn_${parseItem(item).noteNumber}`" id="ref1">{{
              parseItem(item).noteNumber
            }}</a></sup
          >
        </span>
      </li>
    </ul>

    <div class="flex flex-col mt-4 sm:mt-6">
      <div
        v-for="btn in model.buyButtons"
        :key="`btn_${model.position}_${btn.position}`"
        aria-describedby="pricing"
        class="w-full flex"
      >
        <div class="w-full flex flex-col items-center" v-if="btn.linkType === 'shop'">
          <a
            :href="defaultCheckoutConfig.checkoutUrl"
            :title="defaultCheckoutConfig.checkoutVendor"
            target="_blank"
            tabindex="0"
            class="w-full rounded-md outline bg-primary content-primary hover:bg-primary px-3 py-2 text-center text-sm/6 font-semibold shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
          >
            {{ btn.text }}
          </a>
          <span class="text-neutral mt-2">
            {{ otherCheckoutConfig.alternativeCheckoutText }}
            <a
              :href="otherCheckoutConfig.checkoutUrl"
              :title="otherCheckoutConfig.checkoutVendor"
              target="_blank"
              tabindex="0"
              class="w-full text-primary hover:text-primary text-sm/6 font-semibold"
            >
              Checkout Here
            </a>
          </span>
        </div>
        <NuxtLink
          v-else
          :to="`${getLocalizedPathPrefix(lcid)}/releases#top`"
          tabindex="0"
          class="rounded-md w-full bg-primary content-primary hover:bg-primary px-3 py-2 text-center text-sm/6 font-semibold shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
        >
          {{ btn.text }}
        </NuxtLink>
      </div>
    </div>
  </div>
</template>
